<template>
  <!-- DESKTOP -->
  <Menu v-if="brandconfig.BRAND === 'xs2event'" as="div" class="relative">
    <div v-if="userStore.isUser" class="flex items-center">
      <MenuButton
        data-testid="menu-button"
        class="group flex items-center gap-1 text-menu-link"
        aria-label="Open Account Menu">
        <!-- User name -->
        <UserIcon class="size-5 group-hover:scale-105 transition" />
        <span v-if="!userStore.isIncognito" class="hidden lg:block text-base"> {{ userStore.distributor_name }}</span>
        <ChevronDownIcon class="size-3" />
      </MenuButton>
    </div>
    <div v-else-if="!userStore.isWebshop">
      <RouterLink to="/sign-in">{{ $t("Sign in") }}</RouterLink>
    </div>

    <MenuItems
      class="absolute right-0 z-20 mt-2 w-fit origin-top-right rounded-md bg-surface shadow-lg ring-1 ring-black/5 focus:outline-none overflow-hidden">
      <!-- Header (identity) -->
      <div class="px-4 py-3 border-b border-border/40">
        <p class="text-sm font-medium text-foreground truncate">{{ userStore.distributor_name }}</p>
        <p v-if="!userStore.isIncognito" class="text-xs text-foreground/70 truncate">{{ userStore.username }}</p>
      </div>

      <MenuItem v-if="!userStore.isIncognito" v-slot="{ active }">
        <RouterLink
          to="/user/profile"
          :class="[active ? 'bg-gray-100' : '', 'flex items-center px-4 py-2 text-base text-foreground']">
          <UserIcon class="h-6 mr-2" aria-hidden="true" />
          <span class="whitespace-nowrap">{{ $t("Manage account") }}</span>
        </RouterLink>
      </MenuItem>

      <MenuItem v-else v-slot="{ active }">
        <div
          :class="[
            active ? 'bg-gray-100 cursor-not-allowed' : '',
            'flex items-center px-4 py-2 text-base text-foreground',
          ]">
          <UserIcon class="h-6 mr-2" aria-hidden="true" />
          <span class="whitespace-nowrap">{{ $t("Manage account") }}</span>
        </div>
      </MenuItem>

      <MenuItem v-if="userStore.isDistributor" v-slot="{ active }">
        <RouterLink
          to="/user/bookings"
          :class="[active ? 'bg-gray-100' : '', 'flex items-center px-4 py-2 text-base text-foreground']">
          <CalendarDaysIcon class="h-6 mr-2" aria-hidden="true" />
          <span class="whitespace-nowrap">{{ $t("Bookings") }}</span>
        </RouterLink>
      </MenuItem>

      <template v-if="MARKETPLACE">
        <MenuItem v-if="userStore.isSupplier && userStore.roles.includes('supplier')" v-slot="{ active }">
          <RouterLink
            :to="{ name: 'listings', query: { sorting: 'created:desc' } }"
            :class="[active ? 'bg-gray-100' : '', 'flex items-center px-4 py-2 text-base text-foreground']">
            <ArrowPathIcon class="h-6 mr-2" aria-hidden="true" />
            <span class="whitespace-nowrap">{{ $t("Listings & Sales") }}</span>
          </RouterLink>
        </MenuItem>
      </template>

      <MenuItem v-slot="{ active }">
        <RouterLink
          :to="{ name: 'invoices', query: { sorting: 'created:desc' } }"
          :class="[active ? 'bg-gray-100' : '', 'flex items-center px-4 py-2 text-base text-foreground']">
          <WalletIcon class="h-6 mr-2" aria-hidden="true" />
          <span class="whitespace-nowrap">{{ $t("Open invoices") }}</span>
        </RouterLink>
      </MenuItem>

      <MenuItem v-slot="{ active }">
        <RouterLink
          :to="{ name: 'allocations', query: { sorting: 'created:desc' } }"
          :class="[active ? 'bg-gray-100' : '', 'flex items-center px-4 py-2 text-base text-foreground']">
          <UserGroupIcon class="h-6 mr-2" aria-hidden="true" />
          <span class="whitespace-nowrap">{{ $t("Allocations") }}</span>
        </RouterLink>
      </MenuItem>

      <MenuItem v-slot="{ active }">
        <RouterLink
          :to="{ name: 'watchlist' }"
          :class="[active ? 'bg-gray-100' : '', 'flex items-center px-4 py-2 text-base text-foreground']">
          <BellIcon class="h-6 mr-2" aria-hidden="true" />
          <span class="whitespace-nowrap">{{ $t("Watchlist") }}</span>
        </RouterLink>
      </MenuItem>

      <MenuItem v-if="userStore.isAdmin" v-slot="{ active }">
        <button
          :class="[active ? 'bg-gray-100' : '', 'w-full flex items-center px-4 py-2 text-base text-foreground']"
          @click="showImperonsationDialog = true">
          <EyeIcon class="h-6 mr-2" aria-hidden="true" />
          <span class="whitespace-nowrap">{{ $t("Impersonate") }}</span>
        </button>
      </MenuItem>

      <MenuItem v-if="isIT && getMissingKeysCount() > 0 && getLocale() === 'en'" v-slot="{ active }">
        <button
          :class="[active ? 'bg-gray-100' : '', 'flex items-center px-4 py-2 text-base text-foreground']"
          @click="exportMissingKeys()">
          <ArrowDownCircleIcon class="h-6 mr-2" />
          <span class="whitespace-nowrap">{{ $t("Missing translations") }} ({{ getMissingKeysCount() }})</span>
        </button>
      </MenuItem>

      <MenuItem>
        <button
          data-testid="sign-out-button"
          class="m-4 text-base font-medium hover:shadow block p-2 gap-1 rounded bg-slate-50"
          @click="userStore.signOut()">
          <span>{{ $t("Sign out") }}</span>
        </button>
      </MenuItem>
    </MenuItems>
  </Menu>
  <CurrencyChangeModal :open="showModal" :currency="selectedCurrency" />
  <ImpersonationDialog :open="showImperonsationDialog" :api="api" @close="showImperonsationDialog = false" />
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { RouterLink } from "vue-router";
import {
  UserIcon,
  CalendarDaysIcon,
  WalletIcon,
  UserGroupIcon,
  ArrowPathIcon,
  BellIcon,
  EyeIcon,
  ChevronDownIcon,
  ArrowDownCircleIcon,
} from "@heroicons/vue/24/outline";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import { MARKETPLACE } from "@/config/feature-toggles";
import CurrencyChangeModal from "./CurrencyChangeModal.vue";
import { useUserStore } from "@/store/user.js";
import brandconfig from "@/brandconfig";
import { exportMissingKeys, getMissingKeysCount } from "@/i18n";
import { getLocale } from "@/components/page/menu/locales";
import { ImpersonationDialog } from "@xs2event/js-lib/impersonation";
import { api } from "@/helpers/api";

const showImperonsationDialog = ref(false);

const userStore = useUserStore();
const showModal = ref();
const selectedCurrency = ref();

selectedCurrency.value = userStore.selectedCurrency;

const isIT = computed(() => userStore.roles.includes("it"));
</script>
